import { useEffect, useRef, useState } from 'react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { JobCrewScope, SeriesEmployeePay } from '@/lib/api/jobs';
import { fillTemplate, formatMoney } from '@/lib/utils';

import { ScopeSelect } from './scope-select';

interface Props {
  row: SeriesEmployeePay;
  busy: boolean;
  onChangePay: (payAmount: number, scope: JobCrewScope) => void;
  onRemove: (scope: JobCrewScope) => void;
}

type Panel = 'none' | 'pay' | 'remove';

export function CrewEmployeeRow({ row, busy, onChangePay, onRemove }: Props) {
  const { t } = useLanguage();
  const [panel, setPanel] = useState<Panel>('none');
  const [pay, setPay] = useState('');
  const [payScope, setPayScope] = useState<JobCrewScope>('upcoming');
  const [removeScope, setRemoveScope] = useState<JobCrewScope>('upcoming');
  const [armed, setArmed] = useState(false);
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
    },
    [],
  );

  function toggle(next: Panel) {
    setPanel((cur) => (cur === next ? 'none' : next));
    setArmed(false);
  }

  function submitPay() {
    const n = Number(pay);
    if (!pay.trim() || !Number.isFinite(n) || n < 0) return;
    onChangePay(n, payScope);
    setPanel('none');
    setPay('');
  }

  function submitRemove() {
    if (armTimerRef.current) clearTimeout(armTimerRef.current);
    if (armed) {
      setArmed(false);
      onRemove(removeScope);
      setPanel('none');
      return;
    }
    setArmed(true);
    armTimerRef.current = setTimeout(() => setArmed(false), 4000);
  }

  return (
    <li
      className="flex flex-col gap-2 border-b pb-2 last:border-b-0"
      data-testid={`series-crew-${row.employee_id}`}
    >
      <div className="flex items-center gap-3 text-sm">
        <span className="flex-1 min-w-0 truncate">{row.full_name}</span>
        <span className="text-xs text-muted-foreground shrink-0">
          {fillTemplate(t.seriesLaborVisits, { n: row.visits_assigned })} ·{' '}
          {formatMoney(row.total_pay)}
        </span>
        <button
          type="button"
          onClick={() => toggle('pay')}
          disabled={busy}
          className="text-xs text-primary hover:underline disabled:opacity-50"
          data-testid={`series-crew-changepay-${row.employee_id}`}
        >
          {t.seriesChangePay}
        </button>
        <button
          type="button"
          onClick={() => toggle('remove')}
          disabled={busy}
          className="text-xs text-destructive hover:underline disabled:opacity-50"
          data-testid={`series-crew-remove-${row.employee_id}`}
        >
          {t.seriesRemoveFromSeries}
        </button>
      </div>

      {panel === 'pay' && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-2">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={pay}
            onChange={(e) => setPay(e.target.value)}
            aria-label={t.jobPayAmount}
            placeholder="0.00"
            className="w-28"
            data-testid={`series-crew-pay-input-${row.employee_id}`}
          />
          <ScopeSelect
            id={`series-crew-pay-scope-${row.employee_id}`}
            value={payScope}
            onChange={setPayScope}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy || !pay.trim()}
            onClick={submitPay}
            data-testid={`series-crew-pay-apply-${row.employee_id}`}
          >
            {t.seriesApplyPay}
          </Button>
        </div>
      )}

      {panel === 'remove' && (
        <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-2">
          <p className="text-xs text-muted-foreground">{t.seriesRemoveHint}</p>
          <div className="flex flex-wrap items-end gap-2">
            <ScopeSelect
              id={`series-crew-remove-scope-${row.employee_id}`}
              value={removeScope}
              onChange={setRemoveScope}
            />
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={submitRemove}
              data-testid={`series-crew-remove-confirm-${row.employee_id}`}
            >
              {armed ? t.seriesRemoveConfirm : t.seriesRemoveFromSeries}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
